
export interface VocabularyQuestion {
  word: string;
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  type: 'definition' | 'translation' | 'synonym' | 'antonym' | 'usage';
}

export interface VocabularyAnalysis {
  words: Array<{
    word: string;
    definition: string;
    translation: string;
    difficulty: 'easy' | 'medium' | 'hard';
    context: string;
  }>;
  totalWords: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

/**
 * Analyze song lyrics to extract vocabulary words using OpenAI
 */
export const analyzeSongVocabulary = async (lyrics: string, language: string = 'spanish'): Promise<VocabularyAnalysis> => {
  try {
    const response = await fetch('/api/analyze-song-vocabulary', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lyrics, language }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Vocabulary analysis failed');
    }

    const analysis = await response.json();
    
    // Validate the response structure
    if (!analysis.words || !Array.isArray(analysis.words)) {
      throw new Error('Invalid vocabulary analysis format');
    }

    return analysis;
  } catch (error: any) {
    console.error('Error analyzing song vocabulary:', error);
    
    if (error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to analyze song vocabulary');
    }
  }
};

/**
 * Generate vocabulary quiz questions using OpenAI
 */
export const generateVocabularyQuestions = async (
  vocabularyAnalysis: VocabularyAnalysis, 
  numberOfQuestions: number = 8,
  language: string = 'spanish'
): Promise<VocabularyQuestion[]> => {
  try {
    const response = await fetch('/api/generate-vocabulary-questions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vocabularyAnalysis, numberOfQuestions, language }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Question generation failed');
    }

    const result = await response.json();
    
    // Handle both array and object responses
    const exercises = result.exercises || [];
    
    if (!Array.isArray(exercises) || exercises.length === 0) {
      throw new Error('Invalid question format received');
    }

    return exercises;
  } catch (error: any) {
    console.error('Error generating vocabulary questions:', error);
    
    if (error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to generate vocabulary questions');
    }
  }
};

/**
 * Generate additional options for a vocabulary question
 */
export const generateVocabularyOptions = async (
  word: string,
  correctAnswer: string,
  questionType: string,
  language: string = 'spanish'
): Promise<string[]> => {
  try {
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      throw new Error('OpenAI API key is not configured');
    }

    const response = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content: `You are a ${language} language expert. Generate plausible but incorrect options for vocabulary quiz questions.`
        },
        {
          role: "user",
          content: `For the ${language} word "${word}" in a ${questionType} question, the correct answer is "${correctAnswer}".
          
          Generate 3 plausible but incorrect options that could confuse students. Make them realistic mistakes that students might make.
          
          Return ONLY a JSON object with the options:
          {
            "options": ["option1", "option2", "option3"]
          }`
        }
      ],
      temperature: 0.5,
      max_tokens: 200,
      response_format: { type: "json_object" }
    });

    if (!response.choices[0].message.content) {
      throw new Error('No options received from OpenAI');
    }

    const result = JSON.parse(response.choices[0].message.content);
    const options = Array.isArray(result) ? result : result.options || [];
    
    // Combine correct answer with wrong options and shuffle
    const allOptions = [correctAnswer, ...options.slice(0, 3)];
    return shuffleArray(allOptions);
  } catch (error: any) {
    console.error('Error generating vocabulary options:', error);
    
    // Fallback to basic options if AI fails
    return generateFallbackVocabularyOptions(word, correctAnswer, questionType, language);
  }
};

/**
 * Helper function to select words for questions with balanced difficulty
 */
function selectWordsForQuestions(words: any[], numberOfQuestions: number): any[] {
  if (words.length <= numberOfQuestions) {
    return words;
  }

  // Group by difficulty
  const easy = words.filter(w => w.difficulty === 'easy');
  const medium = words.filter(w => w.difficulty === 'medium');
  const hard = words.filter(w => w.difficulty === 'hard');

  // Aim for balanced distribution
  const easyCount = Math.ceil(numberOfQuestions * 0.4);
  const mediumCount = Math.ceil(numberOfQuestions * 0.4);
  const hardCount = numberOfQuestions - easyCount - mediumCount;

  const selected = [
    ...easy.slice(0, easyCount),
    ...medium.slice(0, mediumCount),
    ...hard.slice(0, hardCount)
  ];

  // Fill remaining slots if needed
  while (selected.length < numberOfQuestions && words.length > selected.length) {
    const remaining = words.filter(w => !selected.includes(w));
    if (remaining.length > 0) {
      selected.push(remaining[0]);
    } else {
      break;
    }
  }

  return selected.slice(0, numberOfQuestions);
}

/**
 * Helper function to shuffle array
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Fallback function to generate basic vocabulary options
 */
function generateFallbackVocabularyOptions(
  word: string, 
  correctAnswer: string, 
  questionType: string,
  language: string = 'spanish'
): string[] {
  let wrongOptions: string[] = [];
  
  // Generate plausible wrong options based on question type
  switch (questionType) {
    case 'definition':
      wrongOptions = [
        `Una forma de ${word} que no existe`,
        `Lo contrario de ${word}`,
        `Un tipo de comida relacionada con ${word}`
      ];
      break;
    case 'translation':
      wrongOptions = [
        word + 'ing',
        word + 'ed',
        'the ' + word
      ];
      break;
    case 'synonym':
      wrongOptions = [
        `similar a ${word}`,
        `parecido a ${word}`,
        `como ${word} pero diferente`
      ];
      break;
    case 'antonym':
      wrongOptions = [
        `opuesto a ${word}`,
        `contrario de ${word}`,
        `no ${word}`
      ];
      break;
    case 'usage':
      wrongOptions = [
        `Se usa ${word} cuando...`,
        `${word} se aplica a...`,
        `${word} significa...`
      ];
      break;
    default:
      wrongOptions = [
        `Opción incorrecta 1 para ${word}`,
        `Opción incorrecta 2 para ${word}`,
        `Opción incorrecta 3 para ${word}`
      ];
  }
  
  // Combine and shuffle
  return shuffleArray([correctAnswer, ...wrongOptions]);
}

/**
 * Validate if OpenAI service is available
 */
export const isVocabularyServiceAvailable = (): boolean => {
  return true; // Always available through backend
};

/**
 * Get service status information
 */
export const getVocabularyServiceStatus = () => {
  return {
    available: true,
    apiKey: true, // API key is handled by backend
    model: 'gpt-4-turbo-preview'
  };
};